import { MAX_COLLAPSED_LENGTH, MAX_COLLAPSED_LINES } from "./MeasurementRequestDetails.values";

const displayDate = (dateToDisplay: Date) => {
  return `${dateToDisplay.getUTCDate()}/${dateToDisplay.getUTCMonth() + 1}/${dateToDisplay.getUTCFullYear()}`;
};

const formatTimeForDisplay = (value: Date) => {
  return value.toLocaleString();
};

const formatDieLabel = (dieRow: number, dieColumn: number) => {
  return `${dieRow} | ${dieColumn}`;
};

const calculateProgressPercentage = (step: number | null, maxStep: number | null) => {
  if (step === null || maxStep === null || maxStep === 0) {
    return null;
  }

  return (step / maxStep) * 100;
};

const formatProgressPercentage = (value: number | null) => {
  if (value === null) {
    return "---";
  }

  return Number(value.toFixed(1)).toString();
};

const toCsvValue = (value: string | number | boolean | null) => {
  if (value === null) {
    return "";
  }

  const stringValue = typeof value === "boolean" ? (value ? "true" : "false") : String(value);
  const escaped = stringValue.replace(/"/g, '""');
  return `"${escaped}"`;
};

const formatDiagnosticValue = (value: string | number | boolean | null) => {
  if (value === null) {
    return "---";
  }

  if (typeof value === "boolean") {
    return value ? "true" : "false";
  }

  return value;
};

const getGratingPriority = (grating: string) => {
  const normalized = grating.toUpperCase();

  if (normalized.includes("IN")) {
    return 0;
  }

  if (normalized.includes("OUT")) {
    return 1;
  }

  return 2;
};

const formatLogEventMessage = (message: string) => {
  if (!message) {
    return "";
  }

  let formatted = message;

  // Handle JSON-style escaped newlines/tabs/quotes.
  formatted = formatted
    .replace(/\\r\\n/g, "\n")
    .replace(/\\n/g, "\n")
    .replace(/\\r/g, "\r")
    .replace(/\\t/g, "\t")
    .replace(/\\"/g, '"');

  // If the entire message is a JSON-encoded string, decode it.
  try {
    const parsed = JSON.parse(formatted);

    if (typeof parsed === "string") {
      return parsed;
    }

    // For JSON objects/arrays, pretty-print them.
    return JSON.stringify(parsed, null, 2);
  } catch {
    return formatted;
  }
};

const getCollapsedMessage = (message: string) => {
  if (message.length <= MAX_COLLAPSED_LENGTH) {
    return {
      message,
      isTruncated: false,
    };
  }

  const lines = message.split("\n");

  // For single-line messages, truncate from the beginning.
  if (lines.length === 1) {
    return {
      message: `${message.slice(0, MAX_COLLAPSED_LENGTH).trimEnd()}...`,
      isTruncated: true,
    };
  }

  // Keep the first line because it usually contains the actual error.
  const firstLine = lines[0];

  // Keep the final lines because stack traces often contain
  // the most useful information toward the bottom.
  const remainingLength = MAX_COLLAPSED_LENGTH - firstLine.length - 20;

  const lastLines: string[] = [];
  let lastLinesLength = 0;

  for (let i = lines.length - 1; i > 0; i--) {
    const line = lines[i];

    if (
      lastLines.length >= MAX_COLLAPSED_LINES - 2 ||
      lastLinesLength + line.length > remainingLength
    ) {
      break;
    }

    lastLines.unshift(line);
    lastLinesLength += line.length + 1;
  }

  const omittedLines = lines.length - lastLines.length - 1;

  return {
    message: [firstLine, "", `... ${omittedLines} lines omitted ...`, "", ...lastLines].join("\n"),
    isTruncated: true,
  };
};

export {
  calculateProgressPercentage,
  displayDate,
  formatDieLabel,
  formatProgressPercentage,
  formatTimeForDisplay,
  toCsvValue,
  formatDiagnosticValue,
  getGratingPriority,
  formatLogEventMessage,
  getCollapsedMessage,
};
